import { EIP1193Provider, WalletType } from '../models/wallet.types';

/** Identity markers a wallet may set on its provider object. Pre-EIP-6963
 *  this was the only way to distinguish multiple injected wallets that
 *  shared the `window.ethereum` slot. Indexed access ([key: string])
 *  because OKX's marker name has drifted across the OKEx→OKX rebrand
 *  (isOKExWallet / isOkxWallet / isOKXWallet) and we must tolerate all. */
interface ProviderMarkers extends EIP1193Provider {
  isMetaMask?: boolean;
  isTrust?: boolean;
  isTrustWallet?: boolean;
  /** Set by wallets that ship as MetaMask-compat wrappers but are NOT
   *  MetaMask: Coinbase Wallet, Rabby, Trust, OKX. Used to exclude them
   *  from the MetaMask legacy match. */
  isCoinbaseWallet?: boolean;
  isRabby?: boolean;
  /** EIP-5749 / MetaMask multi-provider extension. When multiple wallets
   *  inject at the same time, each exposes itself in this array; the
   *  root `window.ethereum` is then one selected/default provider, which
   *  may or may not be the one the user actually chose. */
  providers?: ProviderMarkers[];
  [key: string]: unknown;
}

/** OKX provider identity markers across the OKEx→OKX rebrand. Different
 *  OKX builds (desktop extension vs mobile in-app browser, old vs new)
 *  set different spellings — match any of them. */
const OKX_MARKERS = ['isOkxWallet', 'isOKXWallet', 'isOKExWallet'] as const;

/** Defensive property read — some wallets implement provider as a Proxy
 *  whose property access throws (Codex round-4 2026-05-21). A throwing
 *  read here would otherwise bubble out of connect/tryReconnect, and
 *  tryReconnect's catch clears the persisted wallet type → silent
 *  session loss. Treat throws as "absent / undefined". */
function safeRead<T>(fn: () => T): T | undefined {
  try {
    return fn();
  } catch {
    return undefined;
  }
}

function readMarker(p: ProviderMarkers, key: keyof ProviderMarkers): unknown {
  return safeRead(() => p[key]);
}

function readProvidersArray(p: ProviderMarkers): ProviderMarkers[] {
  const arr = safeRead(() => p.providers);
  return Array.isArray(arr) ? (arr as ProviderMarkers[]) : [];
}

/** Sane upper bound on a providers[] array length. Real-world devices
 *  rarely have more than a handful of injected wallets at the same time.
 *  Cap prevents a hostile Proxy from reporting Infinity / 10^9 and
 *  pinning the main thread during connect/reconnect (Codex round-7
 *  2026-05-21). */
const MAX_PROVIDERS_SCAN = 16;

/** Clamps a possibly-hostile array length to a finite, sane bound. */
function safeLength(arr: ProviderMarkers[]): number {
  const rawLen = safeRead(() => arr.length);
  return typeof rawLen === 'number' && Number.isFinite(rawLen) && rawLen > 0
    ? Math.min(rawLen, MAX_PROVIDERS_SCAN)
    : 0;
}

/** Defensive .find — iterates a providers[] array via numeric index reads
 *  catching per-element AND iterator/index-trap throws so a single
 *  hostile entry or a Proxy-backed array cannot abort discovery
 *  (Codex round-5 + round-6 2026-05-21). `for...of` is unsafe because
 *  the iterator protocol itself can be trapped. */
function findProvider(
  arr: ProviderMarkers[],
  type: WalletType,
): ProviderMarkers | undefined {
  const len = safeLength(arr);
  for (let i = 0; i < len; i++) {
    const p = safeRead(() => arr[i]);
    if (!p) continue;
    const m = safeRead(() => matches(p, type));
    if (m === true) return p;
  }
  return undefined;
}

/** True when `p` carries any known OKX identity marker. */
function isOkxMarked(p: ProviderMarkers): boolean {
  return OKX_MARKERS.some((m) => readMarker(p, m) === true);
}

/** Returns true when `p` looks like the wallet identified by `type`.
 *  Only used to disambiguate the SHARED `window.ethereum` surface (root
 *  object + `providers[]` array). The dedicated vendor namespaces
 *  (`window.okxwallet` / `window.trustwallet`) skip this entirely —
 *  their occupancy alone is identity.
 *
 *  MetaMask is matched defensively because Coinbase/Rabby/Trust/OKX all
 *  set `isMetaMask` for MetaMask-SDK compatibility — a sole positive on
 *  that flag would reintroduce the cross-binding bug. */
function matches(p: ProviderMarkers | undefined, type: WalletType): boolean {
  if (!p) return false;
  switch (type) {
    case 'metamask':
      return (
        readMarker(p, 'isMetaMask') === true &&
        !readMarker(p, 'isCoinbaseWallet') &&
        !readMarker(p, 'isRabby') &&
        !readMarker(p, 'isTrust') &&
        !readMarker(p, 'isTrustWallet') &&
        !isOkxMarked(p)
      );
    case 'trustwallet':
      return readMarker(p, 'isTrust') === true || readMarker(p, 'isTrustWallet') === true;
    case 'okxwallet':
      return isOkxMarked(p);
    case 'walletconnect':
      return false;
  }
}

/** Wallet-type-specific legacy provider lookup. Used as a fallback when
 *  EIP-6963 announce events do not surface a chosen wallet (older OKX/Trust
 *  mobile in-app browsers, late-announcing extensions, browsers without
 *  EIP-6963 support).
 *
 *  Why type-specific instead of a generic fallback:
 *  Wesley 2026-05-21 — user picks MetaMask on a device that also has OKX
 *  installed. The previous `window.okxwallet ?? window.ethereum` greedy
 *  fallback returned the OKX provider regardless of the user's choice, so
 *  the app talked to OKX while the user watched MetaMask for a prompt that
 *  never arrived. We now refuse to hand back a provider that does not
 *  match the requested wallet type.
 *
 *  Multi-provider injection (Codex 2026-05-21 round-3): pre-EIP-6963
 *  browsers commonly expose `window.ethereum.providers[]` so multiple
 *  extensions can co-exist on the same slot. We must search that array
 *  before falling back to the root object — otherwise a user with both
 *  MetaMask and OKX installed and root-active=OKX would have MetaMask
 *  silently dropped when they pick it.
 */
export function resolveLegacyProvider(
  type: WalletType,
): EIP1193Provider | undefined {
  if (type === 'walletconnect') return undefined;
  const win = globalThis as Record<string, unknown>;

  // Every window-slot read goes through safeRead so a throwing getter on
  // any namespace (Codex round-5 2026-05-21) cannot propagate out of
  // discovery and into tryReconnect's catch — that would otherwise
  // silently clear the persisted wallet type and end the user's session.

  // 1. OKX has a dedicated window.okxwallet slot. This namespace is
  //    vendor-exclusive — only OKX injects there — so its presence is
  //    sufficient identity; we do NOT require an extra isOkxWallet
  //    marker. The earlier marker gate (Codex round-7) broke OKX
  //    connect entirely: OKX's mobile in-app browser does not reliably
  //    set isOkxWallet, so a present-and-working window.okxwallet was
  //    being rejected (Wesley 2026-05-22 "連連接都不行"). A hostile
  //    overwrite of this slot is a same-origin XSS problem that cannot
  //    be defended at the wallet-resolution layer regardless.
  if (type === 'okxwallet') {
    const okx = safeRead(() => win['okxwallet'] as EIP1193Provider | undefined);
    if (okx) return okx;
  }
  // 2. Trust has a dedicated window.trustwallet slot — same reasoning:
  //    vendor-exclusive namespace, presence is identity.
  if (type === 'trustwallet') {
    const trust = safeRead(() => win['trustwallet'] as EIP1193Provider | undefined);
    if (trust) return trust;
  }
  // 3. Multi-provider array on window.ethereum.providers — search by marker.
  const ethereum = safeRead(() => win['ethereum'] as ProviderMarkers | undefined);
  if (ethereum) {
    const fromArray = findProvider(readProvidersArray(ethereum), type);
    if (fromArray) return fromArray;
  }

  // 4. Root window.ethereum — only return it when its markers match the
  //    requested wallet type. The previous greedy fallback that returned
  //    any non-null ethereum is what bound the wrong wallet.
  if (matches(ethereum, type)) return ethereum;

  // No further fallback. A provider that exposes neither a vendor
  // namespace (step 1/2) nor any recognised marker (step 3/4) cannot be
  // attributed to a specific wallet — returning it for an explicit pick
  // would be a guess, and a wrong guess is the cross-binding bug we set
  // out to fix. Fail closed. (OKX always injects window.okxwallet, so
  // its real in-app-browser case is covered by step 1; there is no
  // confirmed OKX build that lands here.)
  return undefined;
}

export function isValidAddress(address: string): boolean {
  return /^0x[0-9a-fA-F]{40}$/.test(address);
}
